import type CommercialRelationship from '../models/commercial-relationship.js';
import type { CommercialRelationshipParty } from '../models/commercial-relationship.js';

// A depth asked for at connect time is tracked on each supplier's party, an ask made later as a request
export function networkAccessCounts(relationship: Pick<CommercialRelationship, 'parties' | 'networkAccessRequests'>) {
  const requests = relationship.networkAccessRequests ?? [];
  const asks = relationship.parties
    .filter((party) => party.partyType === 'SUPPLIER' && party.status !== 'DECLINED')
    .map((party) => party.networkAccessStatus);
  return {
    granted:
      requests.filter((r) => r.status === 'GRANTED' || r.status === 'PARTIALLY_GRANTED').length +
      asks.filter((status) => status === 'GRANTED' || status === 'PARTIALLY_GRANTED').length,
    pending:
      requests.filter((r) => r.status === 'AWAITING_RESPONSE').length +
      asks.filter((status) => status === 'PENDING').length,
    refused: requests.filter((r) => r.status === 'REJECTED' || r.status === 'REVOKED').length,
  };
}

// Until a supplier accepts, ending it is the client taking its request back
export function isUnansweredRequest(parties: CommercialRelationshipParty[], myCompanyId: string | undefined): boolean {
  const mine = parties.find((party) => party.companyId === myCompanyId);
  return (
    mine?.partyType === 'CLIENT' &&
    !parties.some((party) => party.partyType === 'SUPPLIER' && (party.status ?? 'ACCEPTED') === 'ACCEPTED')
  );
}

// null is a deliberate ask for every tier; undefined and 0 mean nothing was asked for.
export function requestedDepthLabel(depth: number | null | undefined): string {
  if (depth === null) return 'Every tier';
  if (!depth) return 'Not requested';
  return `${depth} tier${depth === 1 ? '' : 's'} deep`;
}

// The client's own words for a depth, as it picks one: "them" is the company it invites
export function depthChoiceLabel(depth: number): string {
  if (depth <= 0) return 'Just them';
  if (depth === 1) return 'Their suppliers';
  if (depth === 2) return "Their suppliers' suppliers";
  return `${depth} tiers down`;
}

// The same depth as the supplier reads it: what the client asks to see past them
export function depthAskPhrase(depth: number): string {
  if (depth <= 0) return 'nothing past you';
  if (depth === 1) return 'your suppliers';
  if (depth === 2) return 'your suppliers and their suppliers';
  return `${depth} tiers past you`;
}

// A decline is told only to the other side of the row (connect-review), so a party on the decliner's own side isn't shown it
export function partiesSeenBy<T extends Pick<CommercialRelationshipParty, 'companyId' | 'partyType' | 'status'>>(
  parties: T[],
  viewerCompanyId: string | undefined,
): T[] {
  const viewerType = parties.find((party) => party.companyId === viewerCompanyId)?.partyType;
  return parties.filter(
    (party) =>
      party.companyId === viewerCompanyId ||
      party.status !== 'DECLINED' ||
      (viewerType !== undefined && party.partyType !== viewerType),
  );
}

// A decline that leaves no counterparty ends the relationship, so on an ACTIVE row only my own decline makes it not live for me
export function relationshipListStatus(
  parties: CommercialRelationshipParty[],
  myCompanyId: string,
  openInvitations = 0,
): 'Declined' | 'Awaiting your reply' | 'Invite sent' | null {
  const myStatus = parties.find((party) => party.companyId === myCompanyId)?.status;
  if (myStatus === 'DECLINED') return 'Declined';
  if (myStatus === 'INVITED') return 'Awaiting your reply';
  // A company invited by email isn't a party until it joins
  if (openInvitations > 0 || parties.some((party) => party.status === 'INVITED')) return 'Invite sent';
  return null;
}
